import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import i18n from '@/i18n/config';
import { useAuth } from '@/hooks/useAuth';
import { getInvitationByToken } from '@/services/invitationService';
import {
  calculatePasswordStrength,
  getPasswordStrengthColor,
} from '@/utils/passwordStrength';
import type { User } from '@packages/shared-types';

export function AcceptInvitationPage(): JSX.Element {
  const { token: tokenFromPath } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const tokenFromQuery = searchParams.get('token');
  // Support both path parameter and query parameter
  const token = tokenFromPath ?? tokenFromQuery;
  const { t } = useTranslation();
  const { acceptInvitation } = useAuth();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<User | null>(null);
  const [isLoadingInvitation, setIsLoadingInvitation] = useState(true);
  const acceptSchema = useMemo(
    () =>
      z
        .object({
          firstName: z.string().min(1, t('validation.firstNameRequired')),
          lastName: z.string().min(1, t('validation.lastNameRequired')),
          password: z.string().min(6, t('validation.passwordMin')),
          confirmPassword: z.string().min(6, t('validation.passwordMin')),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: t('auth.acceptInvitation.passwordMismatch'),
          path: ['confirmPassword'],
        }),
    [t, i18n.language],
  );
  type AcceptFormValues = z.infer<typeof acceptSchema>;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AcceptFormValues>({
    resolver: zodResolver(acceptSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password');
  const passwordStrength = useMemo(
    () => calculatePasswordStrength(password || ''),
    [password],
  );

  // Fetch invitation details when component mounts
  useEffect(() => {
    const fetchInvitation = async (): Promise<void> => {
      if (!token) {
        setIsLoadingInvitation(false);
        setErrorMessage(t('auth.acceptInvitation.tokenMissing'));
        return;
      }

      setIsLoadingInvitation(true);
      setErrorMessage(null);
      try {
        const invitationData = await getInvitationByToken(token);
        setInvitation(invitationData);
        const locale =
          invitationData.tenant?.language === 'es' ? 'es' : 'en';
        await i18n.changeLanguage(locale);
        localStorage.setItem('portal_locale', locale);
      } catch (err) {
        setErrorMessage(t('auth.acceptInvitation.invalidToken'));
      } finally {
        setIsLoadingInvitation(false);
      }
    };

    void fetchInvitation();
  }, [token, t]);

  const onSubmit = async (values: AcceptFormValues): Promise<void> => {
    if (!token) {
      setErrorMessage(t('auth.acceptInvitation.tokenMissing'));
      return;
    }

    setErrorMessage(null);
    try {
      await acceptInvitation({ token, ...values });
      toast.success(t('auth.acceptInvitation.success'));
      // Navigate to root, RootRedirect will handle the correct destination
      navigate('/', { replace: true });
    } catch (error) {
      setErrorMessage(t('auth.acceptInvitation.error'));
    }
  };

  if (isLoadingInvitation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('auth.acceptInvitation.title')}</CardTitle>
            <CardDescription>
              {t('auth.acceptInvitation.loading')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-3 pb-8">
            <LoadingLogo size="sm" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('auth.acceptInvitation.title')}</CardTitle>
          <CardDescription>
            {t('auth.acceptInvitation.description')}
          </CardDescription>
          {invitation && invitation.tenant && (
            <div className="mt-6 flex flex-col items-center gap-4 rounded-lg border bg-muted/50 p-4">
              <Avatar className="h-16 w-16 border-2 border-background">
                {invitation.tenant.profileImage ? (
                  <AvatarImage
                    src={invitation.tenant.profileImage}
                    alt={invitation.tenant.name}
                  />
                ) : null}
                <AvatarFallback className="text-lg font-semibold">
                  {invitation.tenant.name
                    .split(' ')
                    .map((n: string) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </AvatarFallback>
              </Avatar>
              <div className="text-center space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {invitation.tenant.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('auth.acceptInvitation.invitedAs')}{' '}
                  <span className="font-medium">{invitation.email}</span>
                </p>
              </div>
            </div>
          )}
          {invitation && !invitation.tenant && (
            <div className="mt-4 rounded-md bg-muted p-3 text-sm">
              <div>
                <span className="font-medium">
                  {t('auth.acceptInvitation.invitedAs')}
                </span>{' '}
                <span className="text-muted-foreground">{invitation.email}</span>
              </div>
            </div>
          )}
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t('auth.acceptInvitation.firstName')}</Label>
              <Input id="firstName" {...register('firstName')} />
              {errors.firstName ? (
                <p className="text-sm text-destructive">
                  {errors.firstName.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{t('auth.acceptInvitation.lastName')}</Label>
              <Input id="lastName" {...register('lastName')} />
              {errors.lastName ? (
                <p className="text-sm text-destructive">
                  {errors.lastName.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.acceptInvitation.password')}</Label>
              <Input id="password" type="password" {...register('password')} />
              {password && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${getPasswordStrengthColor(
                          passwordStrength.strength,
                        )}`}
                        style={{
                          width: `${(passwordStrength.score / 4) * 100}%`,
                        }}
                      />
                    </div>
                    <span
                      className={`text-xs font-medium ${
                        passwordStrength.strength === 'weak'
                          ? 'text-red-600 dark:text-red-400'
                          : passwordStrength.strength === 'medium'
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : passwordStrength.strength === 'strong'
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-green-600 dark:text-green-400'
                      }`}
                    >
                      {t(
                        `auth.acceptInvitation.passwordStrength.${passwordStrength.strength}`,
                      )}
                    </span>
                  </div>
                </div>
              )}
              {errors.password ? (
                <p className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {t('auth.acceptInvitation.confirmPassword')}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword ? (
                <p className="text-sm text-destructive">
                  {errors.confirmPassword.message}
                </p>
              ) : null}
            </div>
            {errorMessage ? (
              <p className="text-sm text-destructive">{errorMessage}</p>
            ) : null}
          </CardContent>
          <CardFooter>
            <Button
              className="w-full"
              type="submit"
              disabled={isSubmitting || !invitation}
            >
              {isSubmitting
                ? t('auth.acceptInvitation.submitting')
                : t('auth.acceptInvitation.submit')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
